import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Users, Package, ShoppingBag, AlertTriangle, ArrowRight } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { adminAPI } from '../../services/api';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const StatCard = ({ icon: Icon, label, value, sub, color = 'var(--gold)' }) => (
  <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
    <div>
      <p style={{ fontSize: '11px', letterSpacing: '0.2em', color: 'var(--white-dim)', marginBottom: '8px' }}>{label}</p>
      <p style={{ fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 400, color: 'var(--white)' }}>{value}</p>
      {sub && <p style={{ fontSize: '11px', color: 'var(--white-faint)', marginTop: '4px' }}>{sub}</p>}
    </div>
    <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: `${color}18`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={20} color={color} />
    </div>
  </div>
);

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAPI.getDashboard().then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const chartData = data?.monthlyRevenue?.map(item => ({
    month: MONTH_NAMES[(item._id.month - 1)],
    revenue: Math.round(item.revenue),
    orders: item.orders,
  })) || [];

  const STATUS_COLORS = { pending: '#f59e0b', processing: '#3b82f6', shipped: '#8b5cf6', delivered: '#10b981', cancelled: '#ef4444' };

  if (loading) return (
    <div style={{ padding: '32px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '24px' }}>
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton" style={{ height: '120px' }} />)}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '32px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '36px', fontWeight: 400 }}>Dashboard</h1>
        <p style={{ color: 'var(--white-dim)', fontSize: '13px', marginTop: '4px' }}>Welcome back, {data ? 'here\'s your store overview' : ''}</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '32px' }}>
        <StatCard icon={TrendingUp} label="TOTAL REVENUE" value={`$${data?.stats?.totalRevenue?.toFixed(0) || 0}`} />
        <StatCard icon={ShoppingBag} label="TOTAL ORDERS" value={data?.stats?.totalOrders || 0} sub={`${data?.stats?.pendingOrders || 0} pending`} color="#3b82f6" />
        <StatCard icon={Package} label="PRODUCTS" value={data?.stats?.totalProducts || 0} color="#8b5cf6" />
        <StatCard icon={Users} label="CUSTOMERS" value={data?.stats?.totalUsers || 0} color="#10b981" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', marginBottom: '24px' }}>
        {/* Revenue Chart */}
        <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 400, marginBottom: '24px' }}>Revenue Overview</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="goldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#c9a84c" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#c9a84c" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(201,168,76,0.1)" />
                <XAxis dataKey="month" stroke="rgba(245,240,232,0.4)" fontSize={11} />
                <YAxis stroke="rgba(245,240,232,0.4)" fontSize={11} tickFormatter={v => `$${v}`} />
                <Tooltip contentStyle={{ background: 'var(--dark-3)', border: '1px solid var(--border)', color: 'var(--white)', fontSize: '12px' }} formatter={v => [`$${v}`, 'Revenue']} />
                <Area type="monotone" dataKey="revenue" stroke="#c9a84c" strokeWidth={2} fill="url(#goldGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--white-faint)', fontSize: '13px' }}>No revenue data yet</div>
          )}
        </div>

        {/* Low Stock */}
        <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <AlertTriangle size={18} color="#f59e0b" />
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 400 }}>Low Stock</h3>
          </div>
          {data?.lowStock?.length === 0 ? (
            <p style={{ fontSize: '13px', color: 'var(--white-dim)' }}>All products are well-stocked ✓</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {data?.lowStock?.map(p => (
                <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '40px', height: '40px', background: 'var(--dark-3)', overflow: 'hidden', flexShrink: 0 }}>
                    {p.images?.[0] && <img src={p.images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</p>
                    <p style={{ fontSize: '11px', color: p.countInStock === 0 ? '#ef4444' : '#f59e0b' }}>{p.countInStock === 0 ? 'OUT OF STOCK' : `${p.countInStock} left`}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      <div style={{ background: 'var(--dark-2)', border: '1px solid var(--border)', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 400 }}>Recent Orders</h3>
          <Link to="/admin/orders" style={{ fontSize: '11px', color: 'var(--gold)', letterSpacing: '0.1em', display: 'flex', alignItems: 'center', gap: '6px' }}>VIEW ALL <ArrowRight size={12} /></Link>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['ORDER', 'CUSTOMER', 'DATE', 'TOTAL', 'STATUS'].map(h => (
                  <th key={h} style={{ fontSize: '10px', letterSpacing: '0.2em', color: 'var(--white-dim)', textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data?.recentOrders?.map(order => (
                <tr key={order._id} style={{ borderBottom: '1px solid rgba(201,168,76,0.05)' }}>
                  <td style={{ padding: '12px', fontSize: '12px', color: 'var(--gold)' }}>{order.orderNumber}</td>
                  <td style={{ padding: '12px', fontSize: '12px' }}>{order.user?.name}</td>
                  <td style={{ padding: '12px', fontSize: '12px', color: 'var(--white-dim)' }}>{new Date(order.createdAt).toLocaleDateString()}</td>
                  <td style={{ padding: '12px', fontSize: '13px', fontFamily: 'var(--font-display)' }}>${order.totalPrice}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{ fontSize: '10px', padding: '3px 10px', border: `1px solid ${STATUS_COLORS[order.orderStatus] || 'var(--border)'}`, color: STATUS_COLORS[order.orderStatus], letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                      {order.orderStatus}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
